import { createClient } from '@supabase/supabase-js';

// Custom event for tournament stats refresh
export const TOURNAMENT_STATS_REFRESH_EVENT = 'throwball_tournament_stats_refresh';

export function emitTournamentStatsRefresh(tournamentId: string) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TOURNAMENT_STATS_REFRESH_EVENT, { detail: { tournamentId } }));
  }
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export async function getTeams() {
  const { data, error } = await supabase
    .from('teams')
    .select(`
      id,
      name,
      short_name,
      color,
      logo_url,
      players (
        id,
        name,
        number,
        team_id
      )
    `)
    .order('name');

  if (error) throw error;
  return data;
}

export async function createMatch(matchData: {
  matchNumber: number;
  date: string;
  matchType: string;
  teamAId: string;
  teamBId: string;
  tossWonById: string;
  tossChoice: string;
  tournamentId: string;
  winningPoint?: number | null;
  goldenPoint?: number | null;
  matchTime?: string | null;
}) {
  if (!matchData.tournamentId) {
    throw new Error('A tournament is required to create a match.');
  }
  const { data, error } = await supabase
    .from('matches')
    .insert({
      match_number: matchData.matchNumber,
      date: matchData.date,
      match_type: matchData.matchType,
      team_a_id: matchData.teamAId,
      team_b_id: matchData.teamBId,
      toss_won_by_id: matchData.tossWonById,
      toss_choice: matchData.tossChoice,
      tournament_id: matchData.tournamentId,
      winning_point: matchData.winningPoint || null,
      golden_point: matchData.goldenPoint || null,
      match_time: matchData.matchTime || null,
      status: 'first-server',
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function scheduleMatch(matchData: {
  matchNumber: number;
  date: string;
  matchType: string;
  teamAId: string;
  teamBId: string;
  tournamentId: string;
  matchTime?: string | null;
  liveStreamUrl?: string | null;
}) {
  if (!matchData.tournamentId) {
    throw new Error('A tournament is required to schedule a match.');
  }
  const { data, error } = await supabase
    .from('matches')
    .insert({
      match_number: matchData.matchNumber,
      date: matchData.date,
      match_type: matchData.matchType,
      team_a_id: matchData.teamAId,
      team_b_id: matchData.teamBId,
      tournament_id: matchData.tournamentId,
      match_time: matchData.matchTime || null,
      live_stream_url: matchData.liveStreamUrl?.trim() || null,
      status: 'scheduled',
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateMatch(matchId: string, matchData: {
  matchNumber?: number;
  date?: string;
  matchType?: string;
  teamAId?: string;
  teamBId?: string;
  matchTime?: string | null;
  liveStreamUrl?: string | null;
}) {
  const updateData: Record<string, unknown> = {};
  if (matchData.matchNumber !== undefined) updateData.match_number = matchData.matchNumber;
  if (matchData.date !== undefined) updateData.date = matchData.date;
  if (matchData.matchType !== undefined) updateData.match_type = matchData.matchType;
  if (matchData.teamAId !== undefined) updateData.team_a_id = matchData.teamAId;
  if (matchData.teamBId !== undefined) updateData.team_b_id = matchData.teamBId;
  if (matchData.matchTime !== undefined) updateData.match_time = matchData.matchTime;
  if (matchData.liveStreamUrl !== undefined) updateData.live_stream_url = matchData.liveStreamUrl?.trim() || null;

  const { data, error } = await supabase
    .from('matches')
    .update(updateData)
    .eq('id', matchId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/** Transition a scheduled match to first-server (toss/server chosen, ready to go live). */
export async function startScheduledMatch(matchId: string, tossWonById: string, tossChoice: string) {
  const { data, error } = await supabase
    .from('matches')
    .update({
      status: 'first-server',
      toss_won_by_id: tossWonById,
      toss_choice: tossChoice,
    })
    .eq('id', matchId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateMatchStatus(matchId: string, status: string, winnerId?: string) {
  const updateData: Record<string, unknown> = { status };
  if (winnerId) {
    updateData.winner_id = winnerId;
  }

  const { data, error } = await supabase
    .from('matches')
    .update(updateData)
    .eq('id', matchId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function createSet(matchId: string, setNumber: number) {
  const { data, error } = await supabase
    .from('sets')
    .upsert(
      { match_id: matchId, set_number: setNumber },
      { onConflict: 'match_id,set_number', ignoreDuplicates: false }
    )
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateSet(setId: string, updateData: {
  scoreA?: number;
  scoreB?: number;
  winnerId?: string | null;
  endedAt?: string;
}) {
  const { data, error } = await supabase
    .from('sets')
    .update({
      score_a: updateData.scoreA,
      score_b: updateData.scoreB,
      winner_id: updateData.winnerId,
      ended_at: updateData.endedAt,
    })
    .eq('id', setId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getSetsByMatch(matchId: string) {
  const { data, error } = await supabase
    .from('sets')
    .select('*')
    .eq('match_id', matchId)
    .order('set_number');

  if (error) throw error;
  return data;
}

export async function createRally(rallyData: {
  setId: string;
  rallyNumber: number;
  servingTeamId: string;
  serverId: string;
  pointWonById: string;
  lostReason: string | null;
  pointWinnerId: string | null;
}) {
  const { data, error } = await supabase
    .from('rallies')
    .insert({
      set_id: rallyData.setId,
      rally_number: rallyData.rallyNumber,
      serving_team_id: rallyData.servingTeamId,
      server_id: rallyData.serverId,
      point_won_by_id: rallyData.pointWonById,
      lost_reason: rallyData.lostReason,
      point_winner_id: rallyData.pointWinnerId,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getRalliesBySet(setId: string) {
  const { data, error } = await supabase
    .from('rallies')
    .select(`
      id,
      set_id,
      rally_number,
      serving_team_id,
      server_id,
      point_won_by_id,
      lost_reason,
      point_winner_id,
      recorded_at
    `)
    .eq('set_id', setId)
    .order('rally_number', { ascending: false });

  if (error) throw error;
  return data;
}

export async function updateRally(rallyId: string, fields: {
  pointWonById?: string;
  lostReason?: string | null;
  pointWinnerId?: string | null;
  serverId?: string;
  servingTeamId?: string;
}) {
  const update: Record<string, unknown> = {};
  if (fields.pointWonById !== undefined) update.point_won_by_id = fields.pointWonById;
  if (fields.lostReason !== undefined) update.lost_reason = fields.lostReason;
  if (fields.pointWinnerId !== undefined) update.point_winner_id = fields.pointWinnerId;
  if (fields.serverId !== undefined) update.server_id = fields.serverId;
  if (fields.servingTeamId !== undefined) update.serving_team_id = fields.servingTeamId;

  const { data, error } = await supabase
    .from('rallies')
    .update(update)
    .eq('id', rallyId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteRally(setId: string, rallyNumber: number) {
  const { error } = await supabase
    .from('rallies')
    .delete()
    .eq('set_id', setId)
    .eq('rally_number', rallyNumber);

  if (error) throw error;
}

/** Insert a new rally at the end of a set. */
export async function addRallyToSet(fields: {
  setId: string;
  servingTeamId: string;
  serverId: string;
  pointWonById: string;
  lostReason: string | null;
  pointWinnerId: string | null;
}) {
  const { data: existing, error: fetchErr } = await supabase
    .from('rallies')
    .select('rally_number')
    .eq('set_id', fields.setId)
    .order('rally_number', { ascending: false })
    .limit(1);
  if (fetchErr) throw fetchErr;
  const nextNumber = existing && existing.length > 0 ? existing[0].rally_number + 1 : 1;

  return createRally({
    setId: fields.setId,
    rallyNumber: nextNumber,
    servingTeamId: fields.servingTeamId,
    serverId: fields.serverId,
    pointWonById: fields.pointWonById,
    lostReason: fields.lostReason,
    pointWinnerId: fields.pointWinnerId,
  });
}

/** Recount scores from rally records and patch the set row. */
export async function recomputeSetScoresFromRallies(
  setId: string,
  teamAId: string,
  teamBId: string
) {
  const { data: rallies, error } = await supabase
    .from('rallies')
    .select('point_won_by_id')
    .eq('set_id', setId);
  if (error) throw error;

  const scoreA = (rallies ?? []).filter(r => r.point_won_by_id === teamAId).length;
  const scoreB = (rallies ?? []).filter(r => r.point_won_by_id === teamBId).length;

  const { error: updateError } = await supabase
    .from('sets')
    .update({ score_a: scoreA, score_b: scoreB })
    .eq('id', setId);
  if (updateError) throw updateError;
  return { scoreA, scoreB };
}

export async function getTournamentData() {
  const { data, error } = await supabase
    .from('matches')
    .select(`
      id,
      match_number,
      date,
      match_type,
      team_a_id,
      team_b_id,
      toss_won_by_id,
      toss_choice,
      status,
      winner_id,
      best_catcher_ids,
      player_of_match_ids,
      player_of_match_team_id,
      sets (
        id,
        set_number,
        score_a,
        score_b,
        winner_id,
        rallies (
          rally_number,
          serving_team_id,
          server_id,
          point_won_by_id,
          lost_reason,
          point_winner_id,
          recorded_at
        )
      )
    `)
    .order('match_number');

  if (error) throw error;
  return data;
}

export async function getTournamentDataById(tournamentId: string) {
  const { data, error } = await supabase
    .from('matches')
    .select(`
      id,
      match_number,
      date,
      match_type,
      team_a_id,
      team_b_id,
      toss_won_by_id,
      toss_choice,
      status,
      winner_id,
      best_catcher_ids,
      player_of_match_ids,
      player_of_match_team_id,
      sets (
        id,
        set_number,
        score_a,
        score_b,
        winner_id,
        rallies (
          rally_number,
          serving_team_id,
          server_id,
          point_won_by_id,
          lost_reason,
          point_winner_id,
          recorded_at
        )
      )
    `)
    .eq('tournament_id', tournamentId)
    .neq('status', 'discarded')
    .order('match_number');

  if (error) throw error;
  return data;
}

export async function getMatchWithDetails(matchId: string) {
  const { data, error } = await supabase
    .from('matches')
    .select(`
      *,
      sets (
        *,
        rallies (
          *
        )
      )
    `)
    .eq('id', matchId)
    .order('set_number', { referencedTable: 'sets', ascending: true })
    .single();

  if (error) throw error;
  return data;
}

// --- Team CRUD ---

export async function createTeam(data: { name: string; shortName: string; color: string; logoUrl?: string | null }) {
  const insertData: Record<string, string> = {
    name: data.name,
    short_name: data.shortName,
    color: data.color,
  };
  if (data.logoUrl !== undefined && data.logoUrl !== null) {
    insertData.logo_url = data.logoUrl;
  }
  const { data: result, error } = await supabase
    .from('teams')
    .insert(insertData)
    .select()
    .single();
  if (error) throw error;
  return result;
}

export async function updateTeam(id: string, data: { name?: string; shortName?: string; color?: string; logoUrl?: string | null }) {
  const update: Record<string, string | null> = {};
  if (data.name !== undefined) update.name = data.name;
  if (data.shortName !== undefined) update.short_name = data.shortName;
  if (data.color !== undefined) update.color = data.color;
  if (data.logoUrl !== undefined) update.logo_url = data.logoUrl;

  const { data: result, error } = await supabase
    .from('teams')
    .update(update)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return result;
}

export async function deleteTeam(id: string) {
  const { error } = await supabase.from('teams').delete().eq('id', id);
  if (error) throw error;
}

// --- Player CRUD ---

export async function createPlayer(data: { teamId: string; name: string; number: number }) {
  const { data: result, error } = await supabase
    .from('players')
    .insert({ team_id: data.teamId, name: data.name, number: data.number })
    .select()
    .single();
  if (error) throw error;
  return result;
}

export async function updatePlayer(id: string, data: { name?: string; number?: number }) {
  const update: Record<string, string | number> = {};
  if (data.name !== undefined) update.name = data.name;
  if (data.number !== undefined) update.number = data.number;

  const { data: result, error } = await supabase
    .from('players')
    .update(update)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return result;
}

export async function deletePlayer(id: string) {
  const { error } = await supabase.from('players').delete().eq('id', id);
  if (error) throw error;
}

export async function updateMatchBestCatcher(matchId: string, bestCatcherIds: string[], tournamentId?: string) {
  const { data, error } = await supabase
    .from('matches')
    .update({ best_catcher_ids: bestCatcherIds })
    .eq('id', matchId)
    .select()
    .single();

  if (error) throw error;

  // Emit refresh event if tournament ID is known
  if (tournamentId) {
    emitTournamentStatsRefresh(tournamentId);
  } else if (data?.tournament_id) {
    emitTournamentStatsRefresh(data.tournament_id);
  }

  return data;
}

// --- Tournament CRUD ---

export async function getTournaments() {
  const { data, error } = await supabase
    .from('tournaments')
    .select('*')
    .order('date', { ascending: false });

  if (error) throw error;
  return data.map(t => ({
    id: t.id,
    name: t.name,
    date: t.date,
    venue: t.venue,
    leagueWinningPoint: t.league_winning_point,
    leagueGoldenPoint: t.league_golden_point,
    playoffWinningPoint: t.playoff_winning_point,
    playoffGoldenPoint: t.playoff_golden_point,
    createdAt: t.created_at,
    updatedAt: t.updated_at,
  }));
}

export async function getTournament(id: string) {
  const { data, error } = await supabase
    .from('tournaments')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return {
    id: data.id,
    name: data.name,
    date: data.date,
    venue: data.venue,
    leagueWinningPoint: data.league_winning_point,
    leagueGoldenPoint: data.league_golden_point,
    playoffWinningPoint: data.playoff_winning_point,
    playoffGoldenPoint: data.playoff_golden_point,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

export async function createTournament(data: {
  name: string;
  date: string;
  venue?: string;
  leagueWinningPoint: number;
  leagueGoldenPoint: number;
  playoffWinningPoint: number;
  playoffGoldenPoint: number;
}) {
  const { data: result, error } = await supabase
    .from('tournaments')
    .insert({
      name: data.name,
      date: data.date,
      venue: data.venue || null,
      league_winning_point: data.leagueWinningPoint,
      league_golden_point: data.leagueGoldenPoint,
      playoff_winning_point: data.playoffWinningPoint,
      playoff_golden_point: data.playoffGoldenPoint,
    })
    .select()
    .single();

  if (error) throw error;
  return {
    id: result.id,
    name: result.name,
    date: result.date,
    venue: result.venue,
    leagueWinningPoint: result.league_winning_point,
    leagueGoldenPoint: result.league_golden_point,
    playoffWinningPoint: result.playoff_winning_point,
    playoffGoldenPoint: result.playoff_golden_point,
    createdAt: result.created_at,
    updatedAt: result.updated_at,
  };
}

export async function updateTournament(id: string, data: {
  name?: string;
  date?: string;
  venue?: string;
  leagueWinningPoint?: number;
  leagueGoldenPoint?: number;
  playoffWinningPoint?: number;
  playoffGoldenPoint?: number;
}) {
  const update: Record<string, unknown> = {};
  if (data.name !== undefined) update.name = data.name;
  if (data.date !== undefined) update.date = data.date;
  if (data.venue !== undefined) update.venue = data.venue || null;
  if (data.leagueWinningPoint !== undefined) update.league_winning_point = data.leagueWinningPoint;
  if (data.leagueGoldenPoint !== undefined) update.league_golden_point = data.leagueGoldenPoint;
  if (data.playoffWinningPoint !== undefined) update.playoff_winning_point = data.playoffWinningPoint;
  if (data.playoffGoldenPoint !== undefined) update.playoff_golden_point = data.playoffGoldenPoint;

  const { data: result, error } = await supabase
    .from('tournaments')
    .update(update)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return {
    id: result.id,
    name: result.name,
    date: result.date,
    venue: result.venue,
    leagueWinningPoint: result.league_winning_point,
    leagueGoldenPoint: result.league_golden_point,
    playoffWinningPoint: result.playoff_winning_point,
    playoffGoldenPoint: result.playoff_golden_point,
    createdAt: result.created_at,
    updatedAt: result.updated_at,
  };
}

export async function deleteTournament(id: string) {
  const { error } = await supabase.from('tournaments').delete().eq('id', id);
  if (error) throw error;
}

// --- Tournament Participants ---

export async function getTournamentParticipants(tournamentId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('tournament_participants')
    .select('team_id')
    .eq('tournament_id', tournamentId);

  if (error) throw error;
  return (data || []).map(p => p.team_id);
}

export async function addTournamentParticipants(tournamentId: string, teamIds: string[]): Promise<void> {
  if (teamIds.length === 0) return;

  const inserts = teamIds.map(teamId => ({
    tournament_id: tournamentId,
    team_id: teamId,
  }));

  const { error } = await supabase
    .from('tournament_participants')
    .insert(inserts);

  if (error) throw error;
}

export async function removeTournamentParticipant(tournamentId: string, teamId: string): Promise<void> {
  const { error } = await supabase
    .from('tournament_participants')
    .delete()
    .eq('tournament_id', tournamentId)
    .eq('team_id', teamId);

  if (error) throw error;
}

export async function setTournamentParticipants(tournamentId: string, teamIds: string[]): Promise<void> {
  // Get current participants
  const current = await getTournamentParticipants(tournamentId);

  // Determine teams to add and remove
  const toAdd = teamIds.filter(id => !current.includes(id));
  const toRemove = current.filter(id => !teamIds.includes(id));

  // Remove teams no longer participating
  for (const teamId of toRemove) {
    await removeTournamentParticipant(tournamentId, teamId);
  }

  // Add new teams
  if (toAdd.length > 0) {
    await addTournamentParticipants(tournamentId, toAdd);
  }
}

export async function canRemoveTeamFromTournament(tournamentId: string, teamId: string): Promise<boolean> {
  // Check if the team has any matches in this tournament
  const { data: matches, error } = await supabase
    .from('matches')
    .select('id')
    .eq('tournament_id', tournamentId)
    .or(`team_a_id.eq.${teamId},team_b_id.eq.${teamId}`)
    .neq('status', 'discarded')
    .limit(1);

  if (error) throw error;
  return (matches?.length || 0) === 0;
}

export async function getTournamentStats(tournamentId: string) {
  const { data: matches, error } = await supabase
    .from('matches')
    .select('id, status')
    .eq('tournament_id', tournamentId)
    .neq('status', 'discarded');

  if (error) throw error;

  return {
    total: matches.length,
    completed: matches.filter(m => m.status === 'match-won').length,
    live: matches.filter(m => m.status === 'live' || m.status === 'set-won' || m.status === 'timeout').length,
    pending: matches.filter(m => m.status === 'setup' || m.status === 'first-server').length,
    scheduled: matches.filter(m => m.status === 'scheduled').length,
  };
}

export async function getMatchesByTournament(tournamentId: string) {
  const { data, error } = await supabase
    .from('matches')
    .select(`
      id,
      match_number,
      broadcast_id,
      date,
      match_type,
      team_a_id,
      team_b_id,
      toss_won_by_id,
      toss_choice,
      status,
      winner_id,
      winning_point,
      golden_point,
      match_time,
      live_stream_url,
      match_label,
      started_at,
      ended_at,
      is_locked,
      created_at,
      current_serving_team_id,
      current_server_id,
      sets (
        id,
        set_number,
        score_a,
        score_b,
        winner_id,
        started_at,
        ended_at
      )
    `)
    .eq('tournament_id', tournamentId)
    .neq('status', 'discarded')
    .order('match_number');

  if (error) throw error;
  return data;
}

/** Update match start/end times */
export async function updateMatchServingTeam(
  matchId: string,
  servingTeamId: string | null,
  serverId: string | null
) {
  // [DIAGNOSTIC] Log before Supabase update
  console.log('[updateMatchServingTeam] BEFORE update:', {
    matchId,
    servingTeamId,
    serverId,
  });

  const { data, error } = await supabase
    .from('matches')
    .update({
      current_serving_team_id: servingTeamId,
      current_server_id: serverId,
    })
    .eq('id', matchId)
    .select('id, current_serving_team_id, current_server_id')
    .single();

  // [DIAGNOSTIC] Log after update
  if (error) {
    console.error('[updateMatchServingTeam] Supabase ERROR:', error);
  } else {
    console.log('[updateMatchServingTeam] AFTER update - returned data:', data);
  }

  if (error) throw error;
}

export async function updateMatchTimes(matchId: string, times: { startedAt?: string; endedAt?: string }) {
  const updateData: Record<string, string | null> = {};
  if (times.startedAt !== undefined) updateData.started_at = times.startedAt;
  if (times.endedAt !== undefined) updateData.ended_at = times.endedAt;

  const { error } = await supabase
    .from('matches')
    .update(updateData)
    .eq('id', matchId);

  if (error) throw error;
}

/** Calculate duration between two timestamps in human-readable format */
export function calculateDuration(startedAt: string | null, endedAt: string | null): string | null {
  if (!startedAt || !endedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = new Date(endedAt).getTime();
  const diffMinutes = Math.round((end - start) / 60000);

  if (diffMinutes < 60) return `${diffMinutes} mins`;
  const hours = Math.floor(diffMinutes / 60);
  const mins = diffMinutes % 60;
  return mins > 0 ? `${hours} hr ${mins} mins` : `${hours} hr`;
}

export interface PointsTableRow {
  teamId: string;
  teamName: string;
  teamShortName: string;
  teamColor: string;
  teamLogoUrl?: string | null;
  played: number;
  wins: number;
  losses: number;
  points: number;
  setsWon: number;
  setsLost: number;
  setsPlayed: number;
  setWinPct: number;
  pointsScored: number;
  pointsConceded: number;
  netPoints: number;
  position: number;
}

export async function getPointsTable(tournamentId: string): Promise<PointsTableRow[]> {
  const cached = _pointsTableCache.get(tournamentId);
  if (cached && Date.now() - cached.ts < QUERY_CACHE_TTL) return cached.data;
  // Fetch all completed league matches with their sets
  const { data: matches, error: mErr } = await supabase
    .from('matches')
    .select(`id, team_a_id, team_b_id, winner_id, match_type, status, sets(id, score_a, score_b, winner_id)`)
    .eq('tournament_id', tournamentId)
    .eq('match_type', 'league')
    .eq('status', 'match-won');
  if (mErr) throw mErr;

  // Fetch all teams for this tournament's matches
  const teamIds = new Set<string>();
  for (const m of (matches ?? [])) {
    teamIds.add(m.team_a_id);
    teamIds.add(m.team_b_id);
  }
  if (teamIds.size === 0) return [];

  const { data: teamsData, error: tErr } = await supabase
    .from('teams')
    .select('id, name, short_name, color, logo_url')
    .in('id', Array.from(teamIds));
  if (tErr) throw tErr;

  const teamMap = new Map<string, { name: string; shortName: string; color: string; logoUrl?: string | null }>(
    (teamsData ?? []).map(t => [t.id, { name: t.name, shortName: t.short_name, color: t.color, logoUrl: t.logo_url }])
  );

  const stats = new Map<string, {
    played: number; wins: number; losses: number;
    setsWon: number; setsLost: number;
    pointsScored: number; pointsConceded: number;
  }>();

  const ensure = (id: string) => {
    if (!stats.has(id)) stats.set(id, { played: 0, wins: 0, losses: 0, setsWon: 0, setsLost: 0, pointsScored: 0, pointsConceded: 0 });
    return stats.get(id)!;
  };

  for (const m of (matches ?? [])) {
    const a = ensure(m.team_a_id);
    const b = ensure(m.team_b_id);
    a.played++;
    b.played++;
    if (m.winner_id === m.team_a_id) { a.wins++; b.losses++; }
    else if (m.winner_id === m.team_b_id) { b.wins++; a.losses++; }

    for (const s of (m.sets ?? [])) {
      a.setsWon += s.winner_id === m.team_a_id ? 1 : 0;
      a.setsLost += s.winner_id === m.team_b_id ? 1 : 0;
      b.setsWon += s.winner_id === m.team_b_id ? 1 : 0;
      b.setsLost += s.winner_id === m.team_a_id ? 1 : 0;
      a.pointsScored += s.score_a;
      a.pointsConceded += s.score_b;
      b.pointsScored += s.score_b;
      b.pointsConceded += s.score_a;
    }
  }

  const rows: Omit<PointsTableRow, 'position'>[] = Array.from(stats.entries()).map(([id, s]) => {
    const team = teamMap.get(id);
    const setsPlayed = s.setsWon + s.setsLost;
    return {
      teamId: id,
      teamName: team?.name ?? id,
      teamShortName: team?.shortName ?? id,
      teamColor: team?.color ?? '#888',
      teamLogoUrl: team?.logoUrl,
      played: s.played,
      wins: s.wins,
      losses: s.losses,
      points: s.wins * 2,
      setsWon: s.setsWon,
      setsLost: s.setsLost,
      setsPlayed,
      setWinPct: setsPlayed > 0 ? Math.round((s.setsWon / setsPlayed) * 1000) / 10 : 0,
      pointsScored: s.pointsScored,
      pointsConceded: s.pointsConceded,
      netPoints: s.pointsScored - s.pointsConceded,
    };
  });

  // Sort: 1) points desc, 2) setWinPct desc, 3) netPoints desc
  rows.sort((a, b) =>
    b.points - a.points ||
    b.setWinPct - a.setWinPct ||
    b.netPoints - a.netPoints
  );

  const result = rows.map((r, i) => ({ ...r, position: i + 1 }));
  _pointsTableCache.set(tournamentId, { data: result, ts: Date.now() });
  return result;
}

export interface PlayerStarRow {
  playerId: string;
  playerName: string;
  teamId: string;
  teamName: string;
  teamShortName: string;
  teamColor: string;
  teamLogoUrl?: string | null;
  throwerStars: number;
  acerStars: number;
  catcherStars: number;
  totalStars: number;
}

const _pointsTableCache = new Map<string, { data: PointsTableRow[]; ts: number }>();
const _playerStarsCache = new Map<string, { data: PlayerStarRow[]; ts: number }>();
const QUERY_CACHE_TTL = 60_000;

export interface PlayerOfMatchWinner {
  playerId: string;
  playerName: string;
  teamId: string;
  teamName: string;
  totalStars: number;
  throwerStars: number;
  acerStars: number;
  catcherStars: number;
}

/** Fetch ALL matches in a tournament with sets+rallies, for stats computation. */
async function getTournamentMatchesWithRallies(tournamentId: string) {
  const { data, error } = await supabase
    .from('matches')
    .select(`
      id, match_number, match_type, status, team_a_id, team_b_id, winner_id,
      best_catcher_ids,
      sets(id, set_number, score_a, score_b, winner_id,
        rallies(id, rally_number, serving_team_id, server_id, point_won_by_id, lost_reason, point_winner_id)
      )
    `)
    .eq('tournament_id', tournamentId)
    .order('match_number', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

async function getTeamsForTournament(tournamentId: string) {
  const matches = await getTournamentMatchesWithRallies(tournamentId);
  const teamIds = new Set<string>();
  for (const m of matches) {
    teamIds.add(m.team_a_id);
    teamIds.add(m.team_b_id);
  }
  if (teamIds.size === 0) return { matches, teamMap: new Map() as Map<string, any>, playerMap: new Map() };
  const { data: teamsData, error } = await supabase
    .from('teams')
    .select('id, name, short_name, color, logo_url, players(id, name, number, team_id)')
    .in('id', Array.from(teamIds));
  if (error) throw error;

  const teamMap = new Map<string, any>();
  const playerMap = new Map<string, { id: string; name: string; teamId: string; }>();
  for (const t of (teamsData ?? [])) {
    teamMap.set(t.id, { ...t, shortName: t.short_name, logoUrl: t.logo_url });
    for (const p of t.players) {
      playerMap.set(p.id, { id: p.id, name: p.name, teamId: t.id });
    }
  }
  return { matches, teamMap, playerMap };
}

/** Compute all player stars across a tournament. Includes all active matches (live, set-won, match-won). Excludes discarded only. */
export async function getPlayerStars(tournamentId: string): Promise<PlayerStarRow[]> {
  const cached = _playerStarsCache.get(tournamentId);
  if (cached && Date.now() - cached.ts < QUERY_CACHE_TTL) return cached.data;
  const { matches, teamMap, playerMap } = await getTeamsForTournament(tournamentId);
  if (!playerMap || playerMap.size === 0) return [];

  const stats = new Map<string, { thrower: number; acer: number; catcher: number; teamId: string }>();
  const ensure = (pid: string, tid: string) => {
    if (!stats.has(pid)) stats.set(pid, { thrower: 0, acer: 0, catcher: 0, teamId: tid });
    return stats.get(pid)!;
  };

  for (const m of matches) {
    // Skip discarded matches only
    if (m.status === 'discarded') continue;

    for (const s of m.sets) {
      for (const r of s.rallies) {
        // Acer Star — server delivers an ACE
        if (r.lost_reason === 'ACE') {
          const teamId = teamMap.get(r.serving_team_id)?.id ?? r.serving_team_id;
          const entry = ensure(r.server_id, teamId);
          entry.acer += 1;
        }
        // Thrower Star — point winner (player who scored the point)
        if (r.point_winner_id) {
          const teamOfWinner = teamMap.get(r.point_won_by_id);
          if (teamOfWinner) {
            const entry = ensure(r.point_winner_id, teamOfWinner.id);
            entry.thrower += 1;
          }
        }
      }
    }
    // Catcher Stars — from best_catcher_ids array (set at any time during match)
    if (m.best_catcher_ids && Array.isArray(m.best_catcher_ids)) {
      for (const pid of m.best_catcher_ids) {
        const p = playerMap.get(pid);
        if (p) {
          const entry = ensure(pid, p.teamId);
          entry.catcher += 1;
        }
      }
    }
  }

  const rows: PlayerStarRow[] = Array.from(stats.entries()).map(([pid, s]) => {
    const player = playerMap.get(pid);
    const team = teamMap.get(s.teamId);
    return {
      playerId: pid,
      playerName: player?.name ?? pid,
      teamId: s.teamId,
      teamName: team?.name ?? s.teamId,
      teamShortName: team?.shortName ?? s.teamId,
      teamColor: team?.color ?? '#888',
      teamLogoUrl: team?.logoUrl,
      throwerStars: s.thrower,
      acerStars: s.acer,
      catcherStars: s.catcher,
      totalStars: s.thrower + s.acer + s.catcher,
    };
  });

  _playerStarsCache.set(tournamentId, { data: rows, ts: Date.now() });
  return rows;
}

/** Compute Player Of The Match for a single match. Eligible: winning team only. Highest total match stars wins (ties allowed). */
export async function getPlayerOfMatch(matchId: string): Promise<PlayerOfMatchWinner[]> {
  console.log('[PlayerOfMatch] matchId=', matchId);

  const { data, error } = await supabase
    .from('matches')
    .select(`
      id, team_a_id, team_b_id, winner_id, status, best_catcher_ids,
      sets(id, rallies(id, server_id, point_won_by_id, lost_reason, serving_team_id, point_winner_id))
    `)
    .eq('id', matchId)
    .single();
  if (error) {
    console.error('[PlayerOfMatch] DB fetch error:', error);
    throw error;
  }
  if (!data) {
    console.log('[PlayerOfMatch] No match data returned');
    return [];
  }
  if (data.status !== 'match-won') {
    console.log('[PlayerOfMatch] Match status is not match-won, status=', data.status);
    return [];
  }
  if (!data.winner_id) {
    console.log('[PlayerOfMatch] No winner_id on match');
    return [];
  }

  console.log('[PlayerOfMatch] winningTeam=', data.winner_id, '(teamA=', data.team_a_id, ', teamB=', data.team_b_id, ')');
  console.log('[PlayerOfMatch] best_catcher_ids=', data.best_catcher_ids);

  const teamIds = [data.team_a_id, data.team_b_id];
  const { data: teamsData, error: tErr } = await supabase
    .from('teams')
    .select('id, name, short_name, players(id, name, team_id)')
    .in('id', teamIds);
  if (tErr) {
    console.error('[PlayerOfMatch] Teams fetch error:', tErr);
    throw tErr;
  }

  const winningTeam = teamsData?.find(t => t.id === data.winner_id);
  if (!winningTeam) {
    console.log('[PlayerOfMatch] winningTeam not found in teamsData');
    return [];
  }

  console.log('[PlayerOfMatch] candidatePlayers=', winningTeam.players.map((p: any) => p.name));

  // Compute stars per player (all players, filter winners later)
  const playerStars = new Map<string, { thrower: number; acer: number; catcher: number; }>();
  const ensure = (pid: string) => {
    if (!playerStars.has(pid)) playerStars.set(pid, { thrower: 0, acer: 0, catcher: 0 });
    return playerStars.get(pid)!;
  };

  let totalRallies = 0;
  for (const s of data.sets) {
    for (const r of s.rallies) {
      totalRallies++;
      if (r.lost_reason === 'ACE' && r.server_id) {
        ensure(r.server_id).acer += 1;
      }
      if (r.point_winner_id) {
        ensure(r.point_winner_id).thrower += 1;
      }
    }
  }
  console.log('[PlayerOfMatch] totalRallies=', totalRallies, 'sets=', data.sets.length);

  if (data.best_catcher_ids && Array.isArray(data.best_catcher_ids)) {
    for (const pid of data.best_catcher_ids) ensure(pid).catcher += 1;
  }

  // Log per-player star breakdown for winning team
  for (const p of winningTeam.players as any[]) {
    const s = playerStars.get(p.id);
    console.log(`[PlayerOfMatch] player=${p.name} thrower=${s?.thrower ?? 0} acer=${s?.acer ?? 0} catcher=${s?.catcher ?? 0} total=${(s?.thrower ?? 0) + (s?.acer ?? 0) + (s?.catcher ?? 0)}`);
  }

  // Filter to winning team players only
  const winningPlayers = winningTeam.players as any[];
  let maxStars = 0;
  for (const p of winningPlayers) {
    const s = playerStars.get(p.id);
    if (!s) continue;
    const total = s.thrower + s.acer + s.catcher;
    if (total > maxStars) maxStars = total;
  }

  console.log('[PlayerOfMatch] maxStars=', maxStars);

  if (maxStars === 0) {
    console.log('[PlayerOfMatch] maxStars=0, no player of match (no stars recorded for winning team)');
    return [];
  }

  const winners = winningPlayers
    .filter(p => {
      const s = playerStars.get(p.id);
      return s && (s.thrower + s.acer + s.catcher) === maxStars;
    })
    .map(p => {
      const s = playerStars.get(p.id)!;
      return {
        playerId: p.id,
        playerName: p.name,
        teamId: winningTeam.id,
        teamName: winningTeam.name,
        totalStars: maxStars,
        throwerStars: s.thrower,
        acerStars: s.acer,
        catcherStars: s.catcher,
      };
    });

  console.log('[PlayerOfMatch] result=', winners.map(w => `${w.playerName} (${w.totalStars} stars: T=${w.throwerStars} A=${w.acerStars} C=${w.catcherStars})`));

  // Persist Player of Match to the match record
  const winnerIds = winners.map(w => w.playerId);
  await supabase
    .from('matches')
    .update({
      player_of_match_ids: winnerIds,
      player_of_match_team_id: winningTeam.id,
    })
    .eq('id', matchId);

  return winners;
}

/** Save Player of Match result directly (used after match completion). */
export async function savePlayerOfMatch(matchId: string, playerIds: string[], teamId: string): Promise<void> {
  const { error } = await supabase
    .from('matches')
    .update({
      player_of_match_ids: playerIds,
      player_of_match_team_id: teamId,
    })
    .eq('id', matchId);
  if (error) throw error;
}

export async function getMatchWithDetailsByNumber(matchNumber: number) {
  const { data, error } = await supabase
    .from('matches')
    .select(`
      *,
      sets (
        *,
        rallies (
          *
        )
      )
    `)
    .eq('match_number', matchNumber)
    .order('set_number', { referencedTable: 'sets', ascending: true })
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function getMatchWithDetailsByBroadcastId(broadcastId: string) {
  const { data, error } = await supabase
    .from('matches')
    .select(`
      *,
      sets (
        *,
        rallies (
          *
        )
      ),
      timeouts (
        id,
        set_number,
        team_id,
        start_timestamp,
        end_timestamp,
        duration_seconds
      )
    `)
    .eq('broadcast_id', broadcastId)
    .order('set_number', { referencedTable: 'sets', ascending: true })
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function getLiveMatches() {
  const { data, error } = await supabase
    .from('matches')
    .select(
      'id, match_number, broadcast_id, date, match_type, team_a_id, team_b_id, status, tournament_id, current_serving_team_id, created_at'
    )
    .neq('status', 'discarded')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function getScheduledMatches() {
  const { data, error } = await supabase
    .from('matches')
    .select(
      'id, match_number, date, match_type, team_a_id, team_b_id, status, tournament_id, match_time, created_at'
    )
    .eq('status', 'scheduled')
    .order('match_number', { ascending: true });

  if (error) throw error;
  return data;
}

export async function getCompletedMatches() {
  const { data, error } = await supabase
    .from('matches')
    .select(
      'id, match_number, date, match_type, team_a_id, team_b_id, status, tournament_id, created_at'
    )
    .eq('status', 'match-won')
    .order('created_at', { ascending: false });

  console.log('[getCompletedMatches] result:', { count: data?.length });
  if (error) throw error;
  return data;
}

export async function discardMatch(matchId: string): Promise<void> {
  // Fetch current state before update
  const { data: before } = await supabase
    .from('matches')
    .select('id, status')
    .eq('id', matchId)
    .maybeSingle();

  console.log('[discardMatch] Before:', { matchId, currentStatus: before?.status });

  const { data, error } = await supabase
    .from('matches')
    .update({ status: 'discarded' })
    .eq('id', matchId)
    .select()
    .single();

  if (error) {
    console.error('[discardMatch] DB error:', error);
    throw error;
  }

  console.log('[discardMatch] After:', {
    newStatus: data?.status,
    rowsAffected: data ? 1 : 0,
  });
}

// --- Match Locking ---

export async function lockMatch(matchId: string): Promise<void> {
  const { error } = await supabase
    .from('matches')
    .update({ is_locked: true })
    .eq('id', matchId);

  if (error) throw error;
}

export async function unlockMatch(matchId: string): Promise<void> {
  const { error } = await supabase
    .from('matches')
    .update({ is_locked: false })
    .eq('id', matchId);

  if (error) throw error;
}

export async function isMatchLocked(matchId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('matches')
    .select('is_locked')
    .eq('id', matchId)
    .single();

  if (error) throw error;
  return data?.is_locked ?? false;
}

async function deleteMatchCascade(matchId: string): Promise<{ ralliesDeleted: number; setsDeleted: number }> {
  const { data: sets } = await supabase.from('sets').select('id').eq('match_id', matchId);
  let ralliesDeleted = 0;
  const setsDeleted = sets?.length ?? 0;
  if (sets && sets.length > 0) {
    for (const set of sets) {
      const { data: rallies } = await supabase.from('rallies').select('id').eq('set_id', set.id);
      if (rallies && rallies.length > 0) {
        await supabase.from('rallies').delete().eq('set_id', set.id);
        ralliesDeleted += rallies.length;
      }
    }
    await supabase.from('sets').delete().eq('match_id', matchId);
  }
  return { ralliesDeleted, setsDeleted };
}

export async function deleteMatchHard(matchId: string): Promise<void> {
  const cascade = await deleteMatchCascade(matchId);
  const { error } = await supabase.from('matches').delete().eq('id', matchId);
  if (error) throw error;
  console.log('[deleteMatchHard]', { matchId, ...cascade });
}

export async function getAllMatches() {
  const { data, error } = await supabase
    .from('matches')
    .select('id, match_number, broadcast_id, date, match_type, team_a_id, team_b_id, status, tournament_id, created_at, is_locked')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function deleteMatchesByStatus(status: string): Promise<number> {
  const { data: matches } = await supabase.from('matches').select('id').eq('status', status);
  if (!matches || matches.length === 0) return 0;
  for (const m of matches) await deleteMatchCascade(m.id);
  const { error } = await supabase.from('matches').delete().eq('status', status);
  if (error) throw error;
  console.log('[deleteMatchesByStatus]', { status, count: matches.length });
  return matches.length;
}

export async function deleteAllMatchesHard(): Promise<number> {
  const { data: matches } = await supabase.from('matches').select('id');
  if (!matches || matches.length === 0) return 0;
  for (const m of matches) await deleteMatchCascade(m.id);
  const { error } = await supabase.from('matches').delete().neq('id', '00000000-0000-0000-0000-000000000000');
  if (error) throw error;
  console.log('[deleteAllMatchesHard]', { count: matches.length });
  return matches.length;
}

/** Find orphan matches: live/awaiting matches without a tournament_id */
export async function getOrphanMatches() {
  const { data, error } = await supabase
    .from('matches')
    .select('id, match_number, date, match_type, team_a_id, team_b_id, status, created_at')
    .in('status', ['live', 'set-won', 'first-server'])
    .is('tournament_id', null)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

/** Assign a tournament to an orphan match */
export async function assignMatchToTournament(matchId: string, tournamentId: string) {
  const { data, error } = await supabase
    .from('matches')
    .update({ tournament_id: tournamentId })
    .eq('id', matchId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// --- Club Logo Storage ---

export async function getClubLogoUrl(): Promise<string | null> {
  const { data, error } = await supabase
    .from('settings')
    .select('value')
    .eq('key', 'club_logo_url')
    .maybeSingle();

  if (error) throw error;
  return data?.value ?? null;
}

export async function uploadClubLogo(file: File, oldLogoUrl: string | null): Promise<string> {
  const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
  const filePath = `logo-${Date.now()}.${fileExt}`;

  // Remove old logo from storage if it exists
  if (oldLogoUrl) {
    const oldPath = oldLogoUrl.split('/').pop();
    if (oldPath) {
      await supabase.storage.from('club-assets').remove([oldPath]).catch(() => {
        // Best effort removal
      });
    }
  }

  const { error: uploadError } = await supabase.storage
    .from('club-assets')
    .upload(filePath, file, { contentType: file.type });

  if (uploadError) throw new Error(uploadError.message);

  const { data: publicUrl } = supabase.storage.from('club-assets').getPublicUrl(filePath);
  const url = publicUrl.publicUrl;

  // Save to settings table
  const { error: upsertError } = await supabase
    .from('settings')
    .upsert({ key: 'club_logo_url', value: url, updated_at: new Date().toISOString() }, { onConflict: 'key' });

  if (upsertError) throw new Error(upsertError.message);
  return url;
}

export async function removeClubLogo(logoUrl: string): Promise<void> {
  const path = logoUrl.split('/').pop();
  if (path) {
    await supabase.storage.from('club-assets').remove([path]).catch(() => {
      // Best effort removal
    });
  }

  const { error } = await supabase
    .from('settings')
    .upsert({ key: 'club_logo_url', value: null, updated_at: new Date().toISOString() }, { onConflict: 'key' });

  if (error) throw new Error(error.message);
}

// --- Team Logo Storage ---

export async function uploadTeamLogo(file: File, oldLogoUrl: string | null): Promise<string> {
  const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
  const filePath = `team-${Date.now()}.${fileExt}`;

  // Remove old logo from storage if it exists
  if (oldLogoUrl) {
    const oldPath = oldLogoUrl.split('/').pop();
    if (oldPath) {
      await supabase.storage.from('team-logos').remove([oldPath]).catch(() => {
        // Best effort removal
      });
    }
  }

  const { error: uploadError } = await supabase.storage
    .from('team-logos')
    .upload(filePath, file, { contentType: file.type });

  if (uploadError) throw new Error(uploadError.message);

  const { data: publicUrl } = supabase.storage.from('team-logos').getPublicUrl(filePath);
  return publicUrl.publicUrl;
}

export async function removeTeamLogo(logoUrl: string): Promise<void> {
  const path = logoUrl.split('/').pop();
  if (path) {
    await supabase.storage.from('team-logos').remove([path]).catch(() => {
      // Best effort removal
    });
  }
}

// --- Broadcast Settings ---

export interface BroadcastSettings {
  id: string;
  activeBroadcastId: string | null;
  defaultTournamentId: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

/** Get the current broadcast settings (single row) */
export async function getBroadcastSettings(): Promise<BroadcastSettings | null> {
  const { data, error } = await supabase
    .from('broadcast_settings')
    .select('*')
    .maybeSingle();

  if (error) {
    console.error('[getBroadcastSettings] Error:', error);
    throw error;
  }

  if (!data) return null;

  return {
    id: data.id,
    activeBroadcastId: data.active_broadcast_id,
    defaultTournamentId: data.default_tournament_id ?? null,
    updatedAt: data.updated_at,
    updatedBy: data.updated_by,
  };
}

/** Update the default tournament shown on the public /live page (admin only) */
export async function setDefaultTournament(tournamentId: string | null): Promise<void> {
  const { error } = await supabase
    .from('broadcast_settings')
    .update({
      default_tournament_id: tournamentId,
      updated_at: new Date().toISOString(),
    })
    .eq('id', '00000000-0000-0000-0000-000000000001');

  if (error) {
    console.error('[setDefaultTournament] Error:', error);
    throw error;
  }
}

/** Update the active broadcast ID (admin only) */
export async function setActiveBroadcast(broadcastId: string | null): Promise<void> {
  const { error } = await supabase
    .from('broadcast_settings')
    .update({
      active_broadcast_id: broadcastId,
      updated_at: new Date().toISOString(),
    })
    .eq('id', '00000000-0000-0000-0000-000000000001');

  if (error) {
    console.error('[setActiveBroadcast] Error:', error);
    throw error;
  }
}

/** Get all available broadcast IDs from matches */
export async function getAvailableBroadcastIds(): Promise<{ broadcastId: string; matchNumber: number; teamAId: string; teamBId: string; status: string }[]> {
  const { data, error } = await supabase
    .from('matches')
    .select('broadcast_id, match_number, team_a_id, team_b_id, status')
    .not('broadcast_id', 'is', null)
    .neq('status', 'discarded')
    .order('match_number', { ascending: true });

  if (error) {
    console.error('[getAvailableBroadcastIds] Error:', error);
    throw error;
  }

  return (data || []).map(m => ({
    broadcastId: m.broadcast_id!,
    matchNumber: m.match_number,
    teamAId: m.team_a_id,
    teamBId: m.team_b_id,
    status: m.status,
  }));
}

// --- Timeouts ---

export interface TimeoutRecord {
  id: string;
  matchId: string;
  setNumber: number;
  teamId: string;
  teamName: string;
  startTimestamp: string;
  endTimestamp: string | null;
  durationSeconds: number | null;
  createdAt: string;
}

/** Start a timeout */
export async function startTimeout(data: {
  matchId: string;
  setNumber: number;
  teamId: string;
  teamName: string;
}): Promise<TimeoutRecord> {
  const { data: result, error } = await supabase
    .from('timeouts')
    .insert({
      match_id: data.matchId,
      set_number: data.setNumber,
      team_id: data.teamId,
      team_name: data.teamName,
      start_timestamp: new Date().toISOString(),
      end_timestamp: null,
      duration_seconds: null,
    })
    .select()
    .single();

  if (error) {
    console.error('[startTimeout] Error:', error);
    throw error;
  }

  // Update match status to 'timeout' so broadcast overlay can detect it
  await supabase.from('matches').update({ status: 'timeout' }).eq('id', data.matchId);

  return {
    id: result.id,
    matchId: result.match_id,
    setNumber: result.set_number,
    teamId: result.team_id,
    teamName: result.team_name,
    startTimestamp: result.start_timestamp,
    endTimestamp: result.end_timestamp,
    durationSeconds: result.duration_seconds,
    createdAt: result.created_at,
  };
}

/** End a timeout */
export async function endTimeout(timeoutId: string, startTimestamp: string): Promise<TimeoutRecord> {
  const endTime = new Date().toISOString();
  const durationSeconds = Math.floor((new Date(endTime).getTime() - new Date(startTimestamp).getTime()) / 1000);

  const { data: result, error } = await supabase
    .from('timeouts')
    .update({
      end_timestamp: endTime,
      duration_seconds: durationSeconds,
    })
    .eq('id', timeoutId)
    .select()
    .single();

  if (error) {
    console.error('[endTimeout] Update error:', error);
    throw error;
  }

  console.log('[TIMEOUT DEBUG] endTimeout UPDATED ROW:', {
    id: result.id,
    match_id: result.match_id,
    set_number: result.set_number,
    team_id: result.team_id,
    start_timestamp: result.start_timestamp,
    end_timestamp: result.end_timestamp,
    duration_seconds: result.duration_seconds,
    created_at: result.created_at,
  });

  // Restore match status to 'live' after timeout ends
  await supabase.from('matches').update({ status: 'live' }).eq('id', result.match_id);

  return {
    id: result.id,
    matchId: result.match_id,
    setNumber: result.set_number,
    teamId: result.team_id,
    teamName: result.team_name,
    startTimestamp: result.start_timestamp,
    endTimestamp: result.end_timestamp,
    durationSeconds: result.duration_seconds,
    createdAt: result.created_at,
  };
}

/** Get all timeouts for a match */
export async function getTimeoutsForMatch(matchId: string): Promise<TimeoutRecord[]> {
  const { data, error } = await supabase
    .from('timeouts')
    .select('*')
    .eq('match_id', matchId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[getTimeoutsForMatch] Error:', error);
    throw error;
  }

  return (data || []).map(t => ({
    id: t.id,
    matchId: t.match_id,
    setNumber: t.set_number,
    teamId: t.team_id,
    teamName: t.team_name,
    startTimestamp: t.start_timestamp,
    endTimestamp: t.end_timestamp,
    durationSeconds: t.duration_seconds,
    createdAt: t.created_at,
  }));
}

/** Get active timeout for a match (if any) */
export async function getActiveTimeout(matchId: string): Promise<TimeoutRecord | null> {
  const { data, error } = await supabase
    .from('timeouts')
    .select('*')
    .eq('match_id', matchId)
    .is('end_timestamp', null)
    .maybeSingle();

  if (error) {
    console.error('[getActiveTimeout] Error:', error);
    throw error;
  }

  if (!data) return null;

  return {
    id: data.id,
    matchId: data.match_id,
    setNumber: data.set_number,
    teamId: data.team_id,
    teamName: data.team_name,
    startTimestamp: data.start_timestamp,
    endTimestamp: data.end_timestamp,
    durationSeconds: data.duration_seconds,
    createdAt: data.created_at,
  };
}

/** Get timeout counts per team for a match (optionally filtered by set number).
 *  Counts only completed (ended) timeouts.
 *  NOTE: Uses JS filtering instead of .not('end_timestamp', 'is', null)
 *  because that Supabase filter is broken in v2.108.2 and returns empty results.
 */
export async function getTimeoutCounts(matchId: string, setNumber?: number): Promise<Map<string, number>> {
  let query = supabase
    .from('timeouts')
    .select('team_id, end_timestamp')
    .eq('match_id', matchId);

  if (setNumber !== undefined) {
    query = query.eq('set_number', setNumber);
  }

  const { data: allData, error } = await query;

  if (error) {
    console.error('[getTimeoutCounts] Error:', error);
    throw error;
  }

  console.log('[TIMEOUT DEBUG] getTimeoutCounts RAW rows:', {
    matchId,
    setNumber,
    rowCount: allData?.length ?? 0,
    rows: allData,
  });

  // Filter in JS: only count completed (ended) timeouts
  const data = (allData || []).filter(t => t.end_timestamp !== null);

  console.log('[TIMEOUT DEBUG] getTimeoutCounts AFTER JS filter:', {
    rowCount: data.length,
    rows: data,
  });

  const counts = new Map<string, number>();
  for (const t of data) {
    counts.set(t.team_id, (counts.get(t.team_id) || 0) + 1);
  }

  console.log('[TIMEOUT DEBUG] getTimeoutCounts FINAL Map:', {
    map: Object.fromEntries(counts),
  });

  return counts;
}

/** Normalizes a player name into a URL-safe slug, e.g. "Nambi Raj" → "nambi-raj". */
export function slugifyPlayerName(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export interface PlayerCareerTeamStint {
  teamId: string;
  teamName: string;
  teamShortName: string;
  teamColor: string;
  teamLogoUrl?: string | null;
  jerseyNumbers: number[];
  matchesPlayed: number;
}

export interface PlayerCareerMatch {
  matchId: string;
  matchNumber: number;
  date: string | null;
  teamShortName: string;
  teamColor: string;
  opponentShortName: string;
  opponentColor: string;
  result: 'W' | 'L' | '-';
  starsInMatch: number;
}

export interface PlayerCareerProfile {
  slug: string;
  displayName: string;
  playerIds: string[];
  teams: PlayerCareerTeamStint[];
  totalMatches: number;
  wins: number;
  losses: number;
  playerOfMatchCount: number;
  stats: {
    totalStars: number;
    throwerStars: number;
    acerStars: number;
    catcherStars: number;
  };
  recentMatches: PlayerCareerMatch[];
}

/**
 * Fetches a player's career stats across all teams/tournaments by matching on slugified name.
 * Players who switch teams get a new DB row — we merge all rows sharing the same name.
 */
export async function getPlayerProfileBySlug(slug: string): Promise<PlayerCareerProfile | null> {
  const { data: allPlayers, error: playersErr } = await supabase
    .from('players')
    .select('id, name, number, team_id, team:teams(id, name, short_name, color, logo_url)');
  if (playersErr) throw playersErr;
  if (!allPlayers) return null;

  const matchingPlayers = (allPlayers as any[]).filter(p => slugifyPlayerName(p.name) === slug);
  if (matchingPlayers.length === 0) return null;

  const playerIds = matchingPlayers.map(p => p.id);
  const displayName = matchingPlayers[0].name;

  const teamMap = new Map<string, PlayerCareerTeamStint>();
  for (const p of matchingPlayers) {
    const t = p.team;
    if (!t) continue;
    if (!teamMap.has(t.id)) {
      teamMap.set(t.id, {
        teamId: t.id, teamName: t.name, teamShortName: t.short_name,
        teamColor: t.color, teamLogoUrl: t.logo_url,
        jerseyNumbers: [], matchesPlayed: 0,
      });
    }
    const entry = teamMap.get(t.id)!;
    if (!entry.jerseyNumbers.includes(p.number)) entry.jerseyNumbers.push(p.number);
  }
  const teamIds = Array.from(teamMap.keys());

  const emptyProfile: PlayerCareerProfile = {
    slug, displayName, playerIds, teams: Array.from(teamMap.values()),
    totalMatches: 0, wins: 0, losses: 0, playerOfMatchCount: 0,
    stats: { totalStars: 0, throwerStars: 0, acerStars: 0, catcherStars: 0 },
    recentMatches: [],
  };
  if (teamIds.length === 0) return emptyProfile;

  const orFilter = teamIds.flatMap(id => [`team_a_id.eq.${id}`, `team_b_id.eq.${id}`]).join(',');
  const { data: matchRows, error: matchErr } = await supabase
    .from('matches')
    .select(`
      id, match_number, date, status, team_a_id, team_b_id, winner_id, player_of_match_ids, best_catcher_ids,
      sets(id, rallies(id, server_id, point_won_by_id, lost_reason, serving_team_id, point_winner_id))
    `)
    .or(orFilter)
    .neq('status', 'discarded')
    .order('match_number', { ascending: false });
  if (matchErr) throw matchErr;
  if (!matchRows || matchRows.length === 0) return emptyProfile;

  const opponentTeamIds = new Set<string>();
  for (const m of matchRows) {
    if (!teamIds.includes(m.team_a_id)) opponentTeamIds.add(m.team_a_id);
    if (!teamIds.includes(m.team_b_id)) opponentTeamIds.add(m.team_b_id);
  }
  const opponentTeamMap = new Map<string, { shortName: string; color: string }>();
  if (opponentTeamIds.size > 0) {
    const { data: oppTeams } = await supabase
      .from('teams').select('id, short_name, color').in('id', Array.from(opponentTeamIds));
    for (const t of oppTeams ?? []) opponentTeamMap.set(t.id, { shortName: t.short_name, color: t.color });
  }

  let throwerTotal = 0, acerTotal = 0, catcherTotal = 0;
  let wins = 0, losses = 0, playerOfMatchCount = 0, totalMatchesCount = 0;
  const recentMatches: PlayerCareerMatch[] = [];

  for (const m of matchRows) {
    const myTeamId = teamIds.includes(m.team_a_id) ? m.team_a_id : (teamIds.includes(m.team_b_id) ? m.team_b_id : null);
    if (!myTeamId) continue;
    const teamEntry = teamMap.get(myTeamId);

    let starsInMatch = 0;
    let participated = false;

    for (const s of m.sets ?? []) {
      for (const r of s.rallies ?? []) {
        if (r.lost_reason === 'ACE' && playerIds.includes(r.server_id)) {
          acerTotal += 1; starsInMatch += 1; participated = true;
        }
        if (r.point_winner_id && playerIds.includes(r.point_winner_id)) {
          throwerTotal += 1; starsInMatch += 1; participated = true;
        }
      }
    }
    if (m.best_catcher_ids && Array.isArray(m.best_catcher_ids)) {
      for (const pid of m.best_catcher_ids) {
        if (playerIds.includes(pid)) { catcherTotal += 1; starsInMatch += 1; participated = true; }
      }
    }
    if (m.player_of_match_ids && playerIds.some(pid => (m.player_of_match_ids as string[]).includes(pid))) {
      playerOfMatchCount += 1;
    }

    if (!participated) continue;
    totalMatchesCount += 1;
    if (teamEntry) teamEntry.matchesPlayed += 1;

    const isWin = m.status === 'match-won' && m.winner_id === myTeamId;
    const isLoss = m.status === 'match-won' && !!m.winner_id && m.winner_id !== myTeamId;
    if (isWin) wins += 1;
    if (isLoss) losses += 1;

    const opponentTeamId = m.team_a_id === myTeamId ? m.team_b_id : m.team_a_id;
    const opponent = teamMap.get(opponentTeamId) ?? opponentTeamMap.get(opponentTeamId);
    recentMatches.push({
      matchId: m.id, matchNumber: m.match_number, date: m.date,
      teamShortName: teamEntry?.teamShortName ?? '',
      teamColor: teamEntry?.teamColor ?? '#888',
      opponentShortName: (opponent as any)?.teamShortName ?? (opponent as any)?.shortName ?? '—',
      opponentColor: (opponent as any)?.teamColor ?? (opponent as any)?.color ?? '#888',
      result: isWin ? 'W' : isLoss ? 'L' : '-',
      starsInMatch,
    });
  }

  return {
    slug, displayName, playerIds, teams: Array.from(teamMap.values()),
    totalMatches: totalMatchesCount, wins, losses, playerOfMatchCount,
    stats: {
      totalStars: throwerTotal + acerTotal + catcherTotal,
      throwerStars: throwerTotal, acerStars: acerTotal, catcherStars: catcherTotal,
    },
    recentMatches,
  };
}
